import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Clock, Bell, Mail, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DemoHint } from '@/components/demo/DemoHint';
import { SoundNotificationSettingsCard } from '@/components/settings/SoundNotificationSettingsCard';
import { NotificationPreferences } from '@/components/settings/NotificationPreferences';
import { ProfilePhotoCard } from '@/components/settings/ProfilePhotoCard';
import { DeleteAccountCard } from '@/components/settings/DeleteAccountCard';
import { Link } from 'react-router-dom';
import { CreditCard } from 'lucide-react';

export default function ProfileSettings() {
  const { user, profile: authProfile } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [showChangeRequest, setShowChangeRequest] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
      setLoading(false);
      return;
    }

    setProfile(data);
    setLoading(false);
  };

  const handleRequestChange = async () => {
    if (!changeReason.trim()) {
      toast.error('Please provide a reason for the change request');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          fmcsa_change_requested: true,
          fmcsa_change_request_reason: changeReason,
          fmcsa_change_requested_at: new Date().toISOString()
        })
        .eq('id', user?.id);

      if (error) throw error;

      toast.success('Change request submitted. Our team will review it shortly.');
      setShowChangeRequest(false);
      setChangeReason('');
      loadProfile();
    } catch (error) {
      console.error('Error submitting change request:', error);
      toast.error('Failed to submit change request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNotificationChange = async (key: string, value: boolean) => {
    if (!user) return;

    // Optimistically update UI
    setProfile((prev: any) => ({ ...prev, [key]: value }));

    const { error } = await supabase
      .from('profiles')
      .update({ [key]: value })
      .eq('id', user.id);

    if (error) {
      console.error('Error updating notification preference:', error);
      toast.error('Failed to update preference');
      // Revert on error
      setProfile((prev: any) => ({ ...prev, [key]: !value }));
    } else {
      toast.success('Preference updated');
    }
  };

  const handleEmailPrefChange = async (key: string, value: boolean) => {
    if (!user) return;
    const current = profile?.email_notification_preferences || {};
    const next = { ...current, [key]: value };

    // Optimistic update
    setProfile((prev: any) => ({ ...prev, email_notification_preferences: next }));

    const { error } = await supabase
      .from('profiles')
      .update({ email_notification_preferences: next })
      .eq('id', user.id);

    if (error) {
      console.error('Error updating email preference:', error);
      toast.error('Failed to update preference');
      setProfile((prev: any) => ({ ...prev, email_notification_preferences: current }));
    } else {
      toast.success('Preference updated');
    }
  };

  const handleBack = () => {
    const userType = authProfile?.user_type;
    if (userType === 'carrier') {
      navigate('/carrier-dashboard');
    } else {
      navigate('/broker-dashboard');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={handleBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <DemoHint hintKey="page.profile" side="bottom" showIndicator>
        <h1 className="text-3xl font-bold">Settings</h1>
        </DemoHint>
      </div>

      {/* Profile Photo — identity avatar, shown only in user's own account chrome */}
      <ProfilePhotoCard />

      {/* In-app sound notifications (toggle + volume) — all roles */}
      <SoundNotificationSettingsCard />

      {/* New unified notification preferences (channel selector + grouped events) */}
      <NotificationPreferences profile={profile} onUpdated={setProfile} />


      {/* Billing & subscription entry point (in-app, required for App Review) */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Billing & Subscription</h2>
              <p className="text-sm text-muted-foreground">
                View your plan, update your payment method, or cancel.
              </p>
            </div>
          </div>
          <Button asChild variant="outline">
            <Link to="/billing">Manage billing</Link>
          </Button>
        </div>
      </Card>

      {/* FMCSA Verification Section — broker/carrier only. Shippers have no
          FMCSA authority, so the whole card is hidden for them. */}
      {(profile.user_type === 'broker' || profile.user_type === 'carrier') && (
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">FMCSA Authority</h2>
          {profile.fmcsa_verified && (
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Verified</span>
            </div>
          )}
        </div>

        {profile.fmcsa_verified ? (
          <div className="space-y-4">
            {/* Read-only FMCSA Data */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Authority Type</label>
                <p className="font-medium">{profile.fmcsa_authority_type || 'N/A'}</p>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">MC Number</label>
                <p className="font-medium">{profile.fmcsa_mc_number || 'N/A'}</p>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">DOT Number</label>
                <p className="font-medium">{profile.fmcsa_dot_number || 'N/A'}</p>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Status</label>
                <p className="font-medium">
                  {profile.fmcsa_status_code === 'A' ? 'Active' : profile.fmcsa_status_code || 'N/A'}
                </p>
              </div>
              
              <div className="col-span-2">
                <label className="text-sm text-muted-foreground">Legal Name</label>
                <p className="font-medium">{profile.fmcsa_legal_name || 'N/A'}</p>
              </div>
              
              {profile.fmcsa_dba_name && (
                <div className="col-span-2">
                  <label className="text-sm text-muted-foreground">DBA Name</label>
                  <p className="font-medium">{profile.fmcsa_dba_name}</p>
                </div>
              )}
              
              <div className="col-span-2">
                <label className="text-sm text-muted-foreground">Verified On</label>
                <p className="font-medium">
                  {profile.fmcsa_verified_at ? new Date(profile.fmcsa_verified_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>

            {/* Change Request Status */}
            {profile.fmcsa_change_requested ? (
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">Change Request Pending</span>
                </div>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Submitted on: {profile.fmcsa_change_requested_at ? new Date(profile.fmcsa_change_requested_at).toLocaleDateString() : 'N/A'}
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
                  Reason: {profile.fmcsa_change_request_reason}
                </p>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                  Our team is reviewing your request. You will be notified once it's processed.
                </p>
              </div>
            ) : (
              <div className="mt-6">
                {!showChangeRequest ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowChangeRequest(true)}
                    className="w-full"
                  >
                    Request Authority Change
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-brand-teal-subtle border border-brand-teal-deep/20 rounded-lg">
                      <div className="flex items-start gap-2 text-brand-teal-deep mb-2">
                        <AlertCircle className="w-5 h-5 mt-0.5" />
                        <div>
                          <p className="font-medium">Request FMCSA Authority Change</p>
                          <p className="text-sm text-brand-teal-deep/80 mt-1">
                            FMCSA authority information is verified and cannot be changed directly. 
                            Please explain why you need this changed, and our team will review your request.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Textarea
                      placeholder="Explain why you need to change your FMCSA authority information..."
                      value={changeReason}
                      onChange={(e) => setChangeReason(e.target.value)}
                      rows={4}
                      className="w-full"
                    />

                    <div className="flex gap-3">
                      <Button
                        onClick={handleRequestChange}
                        disabled={isSubmitting || !changeReason.trim()}
                        className="flex-1"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowChangeRequest(false);
                          setChangeReason('');
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p>FMCSA authority not yet verified</p>
          </div>
        )}
      </Card>
      )}

      {/* Permanent self-serve account deletion */}
      <DeleteAccountCard />
    </div>
  );
}